/**
 * How canopies are handed out and taken back.
 *
 * The *shape* is `canopy-silk.ts`'s; this file owns the pool. One kit builds
 * the three geometries and four materials **once**, and a `CanopyRig` is four
 * objects pointing at them — so four canopies in the sky cost four matrices,
 * not four copies of the same buffers.
 *
 * The pool exists for a sharper reason than tidiness, recorded at
 * `CanopyRig.setVisible`: a rig built at the moment a man jumps is a buffer
 * upload and a pipeline compile on that frame, and this scene will not have it.
 */

import * as THREE from "three/webgpu";
import {
  canopyGeometry,
  lineGeometry,
  undersideGeometry,
  CANOPY_RADIUS_M,
  CANOPY_RINGS,
  CANOPY_SWEEP_RAD,
  GORE_COUNT,
  HARNESS_Y,
  LINE_COLOUR,
  LINE_LENGTH_M,
  PILOT_COLOUR,
  PILOT_HALF_HEIGHT_M,
  PILOT_LENGTH_M,
  PILOT_RADIUS_M,
  RIG_LIFT_M,
  VENT_FRACTION,
} from "./canopy-silk.ts";

export * from "./canopy-silk.ts";

/**
 * How long the silk takes to come down once he is on his feet, seconds.
 *
 * A round canopy does not stay inflated on the ground: it goes over, spills its
 * air and settles. Two seconds is what that reads as at the distances this
 * scene has — long enough to be a collapse rather than a mesh being switched
 * off, short enough that the field is not full of tents.
 */
export const CANOPY_COLLAPSE_SECONDS = 2;

/**
 * How long the settled heap stays before it despawns, seconds.
 *
 * The heap is the feedback the old forever-canopy was reaching for — *this is
 * where he came down* — and six seconds is enough to read it from a chase
 * camera that is still catching up. After that it is removed and its rig
 * returns to the kit; nothing is left in the scene graph.
 */
export const CANOPY_LINGER_SECONDS = 6;


/**
 * How far the load swings under the canopy, radians, and how fast.
 *
 * **Presentation only and declared as such.** The engine's canopy is a point
 * that falls and drifts (`combat/parachute.ts`); it models no pendulum, because
 * a swing changes nothing about where he lands and would have been sim state
 * that two peers had to agree on. This is the visual the physics does not carry,
 * and it can never move the encounter hash.
 */
const SWAY_RAD = 0.12;
const SWAY_PERIOD_S = 4.5;

/**
 * One drawn canopy: the silk, its rigging and the man, sharing the kit's
 * buffers and materials.
 *
 * The group's origin is **the man's boots** — see the module note. Callers
 * place it at `ParachuteState.position` and nothing else.
 */
export interface CanopyRig {
  readonly group: THREE.Group;
  /** Before the canopy blossoms there is no canopy: he is a falling man. */
  setOpen(open: boolean): void;
  /** The pendulum. `amplitude` 0 stops it — a canopy lying in a field does not rock. */
  setSway(elapsedS: number, amplitude: number): void;
  /**
   * 0 inflated, 1 settled on the ground. The silk goes over, spills and flattens
   * onto the field beside him; the sway is the caller's to stop.
   */
  setCollapse(fraction: number): void;
  /**
   * Shows or hides the whole rig.
   *
   * **This, and not attaching and detaching it, is how a canopy comes and
   * goes.** A rig is built once at boot and stays in the scene graph for the
   * life of the flight, because building one at the moment a man jumps means
   * uploading its buffers and compiling its pipeline on that exact frame —
   * which is the hitch `flight-scene.ts`'s `compileAsync` exists to avoid, and
   * the reason `scene-particles.ts` and `scene-shadows.ts` both say in so many
   * words that they are *"always built, whatever the toggle says"*.
   *
   * That is not a theoretical cost. A first draft of AD-021b minted rigs
   * lazily, and in headless chromium the two buffers (6,912 bytes of canopy and
   * 1,080 of pilot) failed to create on the jump frame, three.js threw out of
   * `Geometries.updateAttributes`, and the whole render loop died — the pilot
   * bailed correctly and the HUD froze at 85 % throttle for the rest of the
   * sortie. `lessons.md` has the write-up.
   */
  setVisible(visible: boolean): void;
  /** Puts the silk back the way it was minted: inflated, unswung, at the origin. */
  reset(): void;
  /** Returns this rig to the kit's pool, hidden and reset. */
  release(): void;
}

/**
 * The silk, built once: four buffers and four materials, however many canopies
 * are in the sky.
 */
export interface CanopyKit {
  /**
   * A rig, re-used from the free list when one has been released.
   *
   * **Call this at construction, not when a canopy is wanted** — see
   * `CanopyRig.setVisible`. The rig comes back visible; hide it until it is
   * needed.
   */
  mint(): CanopyRig;
  /** Rigs handed out and not yet released — what the census counts. */
  liveCount(): number;
  /** Buffers this kit has ever built. Constant after construction; the census asserts it. */
  builtCount(): number;
  dispose(): void;
}

class CanopyRigImpl implements CanopyRig {
  readonly group = new THREE.Group();
  /** Everything that swings: the silk, its lines and the man under them. */
  private readonly swing = new THREE.Group();
  /** The silk alone, lifted clear of his boots and collapsed independently of him. */
  private readonly silk = new THREE.Group();

  constructor(
    private readonly kit: CanopyKitImpl,
    private readonly canopy: THREE.Mesh,
    private readonly underside: THREE.Mesh,
    private readonly lines: THREE.LineSegments,
    pilot: THREE.Mesh,
  ) {
    this.silk.add(canopy, underside, lines);
    this.silk.position.y = RIG_LIFT_M;
    pilot.position.y = PILOT_HALF_HEIGHT_M;
    this.swing.add(this.silk, pilot);
    this.group.add(this.swing);
  }

  setOpen(open: boolean): void {
    this.silk.visible = open;
  }

  setSway(elapsedS: number, amplitude: number): void {
    const phase = (elapsedS / SWAY_PERIOD_S) * Math.PI * 2;
    this.swing.rotation.z = Math.sin(phase) * amplitude;
    this.swing.rotation.x = Math.cos(phase * 0.73) * amplitude * 0.6;
  }

  setCollapse(fraction: number): void {
    const t = Math.min(1, Math.max(0, fraction));
    // The canopy spills its air: it spreads a little as it flattens, which is
    // what a round canopy going over actually does, and it sinks down the
    // rigging until the silk is lying beside him rather than above him.
    const flatten = 1 - t * 0.92;
    for (const part of [this.canopy, this.underside]) part.scale.set(1 + t * 0.35, flatten, 1 + t * 0.35);
    this.lines.scale.set(1 + t * 0.35, flatten, 1 + t * 0.35);
    this.silk.position.y = RIG_LIFT_M * (1 - t) + t * 0.15;
  }

  setVisible(visible: boolean): void {
    this.group.visible = visible;
  }

  release(): void {
    this.reset();
    this.setVisible(false);
    this.kit.recycle(this);
  }

  /** Puts a recycled rig back the way `mint` hands one out. */
  reset(): void {
    this.setOpen(true);
    this.setCollapse(0);
    this.setSway(0, 0);
    this.group.position.set(0, 0, 0);
  }
}

class CanopyKitImpl implements CanopyKit {
  private readonly canopy = canopyGeometry();
  private readonly underside = undersideGeometry();
  private readonly lines = lineGeometry();
  private readonly pilot = new THREE.CapsuleGeometry(PILOT_RADIUS_M, PILOT_LENGTH_M, 4, 8);
  private readonly shell = new THREE.MeshBasicNodeMaterial({ vertexColors: true, side: THREE.FrontSide });
  private readonly inner = new THREE.MeshBasicNodeMaterial({ vertexColors: true, side: THREE.BackSide });
  private readonly cord = new THREE.LineBasicMaterial({ color: LINE_COLOUR });
  private readonly figure = new THREE.MeshBasicNodeMaterial({ color: PILOT_COLOUR });
  private readonly free: CanopyRigImpl[] = [];
  private live = 0;
  private built = 0;
  private disposed = false;

  mint(): CanopyRig {
    this.live += 1;
    const recycled = this.free.pop();
    if (recycled) {
      recycled.reset();
      recycled.setVisible(true);
      return recycled;
    }
    this.built += 1;
    return new CanopyRigImpl(
      this,
      new THREE.Mesh(this.canopy, this.shell),
      new THREE.Mesh(this.underside, this.inner),
      new THREE.LineSegments(this.lines, this.cord),
      new THREE.Mesh(this.pilot, this.figure),
    );
  }

  recycle(rig: CanopyRigImpl): void {
    this.live -= 1;
    if (this.disposed) return;
    this.free.push(rig);
  }

  liveCount(): number {
    return this.live;
  }

  builtCount(): number {
    return this.built;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.free.length = 0;
    for (const geometry of [this.canopy, this.underside, this.lines, this.pilot]) geometry.dispose();
    for (const material of [this.shell, this.inner, this.cord, this.figure]) material.dispose();
  }
}

/**
 * The silk, built once. Every canopy in the scene — the player's and every
 * ambience jumper's — is a rig minted from one of these.
 */
export function createCanopyKit(): CanopyKit {
  return new CanopyKitImpl();
}

/**
 * The numbers the tests measure rather than restate: that the gores are two
 * tones, that the skirt flares past a hemisphere, that the vent is a hole, and
 * that the silk hangs above the man's boots rather than below them.
 */
export const PARACHUTE_GEOMETRY = {
  CANOPY_RADIUS_M,
  CANOPY_SWEEP_RAD,
  GORE_COUNT,
  CANOPY_RINGS,
  VENT_FRACTION,
  LINE_LENGTH_M,
  HARNESS_Y,
  PILOT_HALF_HEIGHT_M,
  RIG_LIFT_M,
  SWAY_RAD,
  canopyGeometry,
} as const;
